import {
  pushData,
  pushEvent,
  getNavigationTrackingParams,
} from '@nykaa/data-layer/utils';
import { mixpanelTracking } from '@nykaa/data-layer';
import { doAddToCart } from 'shared/store/addToCart/action';
import { PDP_COMBO_WIDGET, SiteNavigation } from 'shared/constants/dataLayer';
import { CartData } from 'store/addToCart/types';
import { AddToCartParams } from 'store/addToCart/action';
import { CAB_SECTION_ID } from 'shared/constants/productPage';
import { showSignUp } from 'store/app/actions';
import { wishlistAdd, wishlistRemove } from 'layouts/Product/Actions/actions';
import {
  SCROLL_BEHAVIOR,
  COMBO_QUERY_PARAM,
  REDIRECT_FROM,
} from 'layouts/Product/Actions/constants';
import { CURRENCY, standardErrorMessages } from 'shared/constants';
import { CTAAddToCartActionProps, CTAWishListActionProps } from './interface';
import {
  ADDTOBAG_WITHOUT_SIZE,
  DEFAULT_PRODUCT_TRACKING,
  EVENT_MAP,
  TRACKING_PAGE_TYPES,
} from './constants';
import { sendSingleEvent } from 'helpers/impressions/sendSingleRetinaEvent';
import {
  CLICK,
  PRODUCT_ADD_TO_WISHLIST,
  PRODUCT_REMOVE_FROM_WISHLIST,
} from '@nykaa/utils/retina/constants';
import { ProductWidgetTrackingProps } from './index';
import { getDevice } from 'helpers/utils/device';
import { getStockString } from 'store/dataLayer/helpers';

export const getWidgetTrackingValues = (
  dnTrackingData: Record<string, any>
) => {
  const { dn_page_type: pageType = '', dn_page_section: widgetName = '' } =
    dnTrackingData;
  const siteNavigation = SiteNavigation.PRODUCT_WIDGETS;
  const siteSubNavigation = `${SiteNavigation.PRODUCT_WIDGETS}:${pageType}:${widgetName}`;
  const interactionLocation = `${SiteNavigation.PRODUCT_WIDGETS}:${pageType}:${widgetName}`;
  return {
    siteNavigation,
    siteSubNavigation,
    interactionLocation,
  };
};

export const handleAddToCart = async (dataProps: CTAAddToCartActionProps) => {
  const {
    isVariantsAvailable,
    product,
    setIsLoading,
    dispatch,
    isPro,
    setAddToBagError,
    setAddToBagMessage,
    handleAddToCartTracking,
    dnTrackingData,
  } = dataProps;
  // if no shade and size triging the event into the data layer
  if (!isVariantsAvailable) {
    pushEvent(ADDTOBAG_WITHOUT_SIZE);
  } else {
    // pushing data to data layer if redirected from pdp combo widget
    const queryParams = new URLSearchParams(location.search);
    let { siteNavigation, siteSubNavigation } =
      getWidgetTrackingValues(dnTrackingData);
    const journeyData = {
      siteNavigation,
      siteSubNavigation,
    };

    if (queryParams.get(REDIRECT_FROM) === COMBO_QUERY_PARAM) {
      siteNavigation = PDP_COMBO_WIDGET;
      siteSubNavigation = `${PDP_COMBO_WIDGET}:${product?.name}`;
      pushData({
        pageName: PDP_COMBO_WIDGET,
        siteNavigation,
        siteSubNavigation,
      });
    }
    const params: AddToCartParams = {
      productId: product?.id,
      isPro,
      trackingParam: getNavigationTrackingParams(
        siteNavigation,
        siteSubNavigation
      ),
    };
    setIsLoading(true);
    try {
      const cartData: CartData = await dispatch(doAddToCart(params));
      handleAddToCartTracking(cartData, product, journeyData);
      setAddToBagError(false);
      setAddToBagMessage(cartData.message);
      sendSingleEvent({
        ...dnTrackingData,
      });
      const cabSectionNode = document.getElementById(CAB_SECTION_ID);
      if (cabSectionNode) {
        cabSectionNode.scrollIntoView({ behavior: SCROLL_BEHAVIOR });
      }
    } catch (e) {
      // Catch clause variable type annotation must be 'any' or 'unknown' if specified
      if (e.errorInApi) {
        setAddToBagMessage(e.message);
      } else {
        setAddToBagMessage(standardErrorMessages.SOMETHING_WRONG);
      }
      setAddToBagError(true);
    }
    setIsLoading(false);
  }
  return;
};

export const handleAddToWishlist = (
  dataProps: CTAWishListActionProps,
  productWidgetProps: ProductWidgetTrackingProps = DEFAULT_PRODUCT_TRACKING
) => {
  const {
    product,
    dispatch,
    email,
    isInWishlist,
    setWishlistMessage,
    setWishlistError,
    user,
    pwPageSource,
    pageLocation,
    primaryCategories,
    brandName,
    videoId,
    trackingData,
    dnTrackingData,
    clickInteraction,
  } = dataProps;
  // pushing data to data layer if redirected from pdp combo widget
  const queryParams = new URLSearchParams(location.search);
  if (queryParams.get(REDIRECT_FROM) === COMBO_QUERY_PARAM) {
    pushData({ pageName: PDP_COMBO_WIDGET });
  }
  if (clickInteraction) {
    pushData({ clickInteraction });
  }
  if (email) {
    if (product?.id) {
      if (isInWishlist) {
        wishlistRemove(
          {
            dispatch,
            setIsInWishlist: () => {}, // this is not required from SDK end, handled using wishlist id's from store
            setWishlistMessage,
            setWishlistError,
            pageLocation,
            brandName,
            product,
            pwPageSource,
            trackingData,
          },
          productWidgetProps
        );
        sendSingleEvent({
          ...dnTrackingData,
          event_type: CLICK,
          event_name: PRODUCT_REMOVE_FROM_WISHLIST,
        });
      } else {
        wishlistAdd(
          {
            dispatch,
            setIsInWishlist: () => {}, // this is not required from SDK end, handled using wishlist id's from store
            setWishlistMessage,
            setWishlistError,
            pageLocation,
            primaryCategories,
            brandName,
            videoId,
            product,
            pwPageSource,
            user,
            trackingData,
          },
          productWidgetProps
        );
        sendSingleEvent({
          ...dnTrackingData,
          event_type: CLICK,
          event_name: PRODUCT_ADD_TO_WISHLIST,
        });
      }
    }
  } else {
    dispatch(showSignUp()); // if user is not loggedin
  }
};

export const isListingPage = (pageName: string | undefined) => {
  if (!pageName) {
    return false;
  }
  const name = pageName?.toLowerCase();
  return (
    name === TRACKING_PAGE_TYPES.PLP || name === TRACKING_PAGE_TYPES.SEARCH
  );
};

export const transformDataforMixPanelTracking = (
  data: any,
  event_name: string,
  productWidgetProps: ProductWidgetTrackingProps = DEFAULT_PRODUCT_TRACKING
) => {
  const { isProductWidget, device, tierName } = productWidgetProps;
  if (device !== 2 && isProductWidget) {
    let trackingData = data[event_name];
    let categoryData = trackingData.categoryLevel || {};
    const cL1 = categoryData.l1 || {};
    const cL2 = categoryData.l2 || {};
    const cL3 = categoryData.l3 || {};
    const product = trackingData?.product || {};
    let datalayer;
    if (typeof window !== 'undefined' && window.datalayer) {
      datalayer = window.datalayer;
    }
    const { siteNavigation, siteSubNavigation, user, saleConfig } = datalayer;
    const {
      isEarlyAccess: is_early_access,
      isFlashSale: is_flash_sale,
      isPrEnabled: is_price_reveal,
    } = saleConfig || {};
    const {
      customerId: nykaa_customer_id,
      isLoggedIn: is_logged_in,
      groupId: pro_user_type,
    } = user || {};

    const finalPayload = {
      brand_name: product?.brandName || trackingData?.brandName || '',
      category_l1_name: cL1.name || data?.category_l1_name || '',
      category_l2_name: cL2.name || data?.category_l2_name || '',
      category_l3_name: cL3.name || data?.category_l3_name || '',
      currency: CURRENCY,
      discounted_percent: product?.discount || trackingData?.discount || '',
      discounted_price: product?.offerPrice || trackingData?.offerPrice || '',
      is_early_access,
      is_flash_sale,
      is_logged_in,
      is_price_reveal,
      mrp_price: product?.mrp || trackingData?.mrp || '',
      nykaa_customer_id,
      nykaa_store: __PLATFORM__,
      pro_user_type,
      product_id: product?.id || trackingData?.productId || '',
      product_name: product?.name || trackingData?.productName,
      product_stock_status:
        getStockString(product?.inStock) || trackingData?.inStock || '',
      product_tags: product?.tags || trackingData?.tags || '',
      product_type: product.type || trackingData?.type || '',
      quantity: product.quantity || 0,
      variant_product_id: product?.childId || trackingData?.childId || '',
      variant_type: product?.variantType || trackingData?.variantType || '',
      site_navigation: data?.site_navigation || siteNavigation,
      site_sub_navigation: data?.site_sub_navigation || siteSubNavigation,
      interaction_location: data?.interaction_location || siteSubNavigation,
      [`${EVENT_MAP[event_name]}_location`]:
        data?.interaction_location || siteSubNavigation,
      source_platform: getDevice(device),
      prive_membership_type: tierName,
      has_nykaa_gpt_interaction: trackingData?.isConvSearch || false,
      gpt_conversation_id: trackingData?.gptConversationId || '',
      listing_type: data?.listing_type || '',
      listing_value: data?.listing_value,
      plp_click_count: data?.plp_click_count || 0,
      search_keyword: data?.search_keyword || '',
      listing_opened: data?.listing_opened || '',
      ...(trackingData?.updated_quantity && {
        updated_quantity: trackingData.updated_quantity,
      }),
      ...(data?.ui_design_version && {
        ui_design_version: data.ui_design_version || '',
      }),
      ...(data?.active_tab && {
        active_tab: data.active_tab || '',
      }),
    };
    mixpanelTracking(device, EVENT_MAP[event_name], finalPayload);
  }
};
